// src/Pages/UserPlanLog/index.jsx

import React, { useState, useEffect } from "react";
import { Flex, Select, DatePicker, Button  } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DownloadOutlined } from "@ant-design/icons";
import DebouncedInput from "../../Component/DebouncedInput";
import CustomTable from "../../Component/Table";
import { FilterContainer } from "../../Styles/filterStyle";
import { getAllUserPlanLog, exportUserPlanLogs } from "../../Services/subscription";
import { formatDate } from "../../utils/date";
import { fetchUsers } from "../../Services/user_question_history";
import CustomFilterSelect from "../../Component/CustomFilterSelect";
import ExpandableText from "../../Component/ExpandText";
const { RangePicker } = DatePicker;

// user_credit_plan_logs.type values (see app/models/credit_subscription.py UserCreditPlanLog)
const LOG_TYPE_OPTIONS = [
    "deduction", "grant_initial", "grant_renewal_reset", "grant_topup",
    "grant_form_bonus", "grant_product_change", "grant_free_trial",
    "refund_adjustment", "admin_adjustment", "cancellation", "uncancellation",
    "expiration", "billing_issue", "transfer", "subscription_paused",
    "cron_partial_expiry", "monthly_limit_updated", "superseded", "product_changed",
].map((v) => ({
    label: v.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
    value: v,
}));


const UserPlanLogList = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedLogType, setSelectedLogType] = useState(null);
    const [dateRange, setDateRange] = useState({
        start_date: null,
        end_date: null,
    });

    const skip = (currentPage - 1) * pageSize;

    // Reset to first page when search term or selected user changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedUserId, selectedLogType, dateRange]);

    const { data, isLoading } = useQuery({
        queryKey: ["logs", currentPage, pageSize, searchTerm, selectedUserId, selectedLogType, dateRange],
        queryFn: () =>
            getAllUserPlanLog({
                name: searchTerm,
                userId: selectedUserId,
                costType: selectedLogType,
                skip,
                limit: pageSize,
                start_date: dateRange.start_date,
                end_date: dateRange.end_date,
                isCreditPlan: true,
            }),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Same filters the table is showing, minus pagination.
    const { mutate: triggerDownload, isPending: isDownloading } = useMutation({
        mutationFn: async () => {
            return await exportUserPlanLogs({
                name: searchTerm,
                userId: selectedUserId,
                costType: selectedLogType,
                start_date: dateRange.start_date,
                end_date: dateRange.end_date,
            });
        },
    });

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const columns = [
        { title: "ID", dataIndex: "id", key: "id" },
        { title: "User Id", dataIndex: "user_id", key: "user_id" },
        { title: "User Name", dataIndex: "user_name", key: "user_name", render: (text) => text || "-" },
        { title: "Plan Slug", dataIndex: "plan_slug", key: "plan_slug", render: (text) => text || "-" },
        {
            title: "Log Type",
            dataIndex: "type",
            key: "type",
            render: (text) =>
                text
                ? text.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
                : "-",
        },
        { title: "Source", dataIndex: "source", key: "source", render: (text) => text || "-" },
        { title: "Credits Delta", dataIndex: "credits_delta", key: "credits_delta" },
        { title: "Credits Balance After", dataIndex: "credits_balance_after", key: "credits_balance_after" },
        { title: "Category", dataIndex: "category_name", key: "category_name", render: (text) => text || "-" },
        { title: "Question Category Id", dataIndex: "question_category_id", key: "question_category_id", render: (text) => text || "-" },
        { title: "Itinerary Id", dataIndex: "itineary_id", key: "itineary_id", render: (text) => text || "-" },
        {
            title: "Log Description",
            dataIndex: "description",
            key: "description",
            render: (text) => <ExpandableText text={text} />,
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (text) => (text ? formatDate(text) : "-"),
        },
        {
            title: "Updated At",
            dataIndex: "updated_at",
            key: "updated_at",
            render: (text) => (text ? formatDate(text) : "-"),
        },
    ];

    const handleDateChange = (dates) => {
        if (dates) {
        setDateRange({
            start_date: dates[0].format("YYYY-MM-DD"),
            end_date: dates[1].format("YYYY-MM-DD"),
        });
        } else {
        setDateRange({
            start_date: null,
            end_date: null,
        });
        }
    };


    return (
        <div>
            <Flex justify="space-between" align="center">
                <h3>User Logs</h3>
                <Button type="primary" icon={<DownloadOutlined />} onClick={triggerDownload} loading={isDownloading}>
                    Export
                </Button>
            </Flex>

            <br />

            <FilterContainer>
                <CustomFilterSelect
                    label="User"
                    fetchOptions={fetchUsers}
                    filterKey="user"
                    onChange={(key, value) => setSelectedUserId(value)}
                    renderOption={(option) => `#${option.value} ${option.label} (${option.email})`}
                    className="custom-user-select"
                />

                <DebouncedInput
                    placeholder="Search by Name..."
                    value={searchTerm}
                    onChange={setSearchTerm}
                    delay={1000}
                    width={180}
                />

                <Select
                    allowClear
                    placeholder="Select Log Type"
                    style={{ width: 200 }}
                    value={selectedLogType}
                    onChange={setSelectedLogType}
                    options={LOG_TYPE_OPTIONS}
                />

                <div >
                    <RangePicker onChange={handleDateChange} />
                </div>
            </FilterContainer>

            <CustomTable
                columns={columns}
                data={data?.logs || []}
                loading={isLoading}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: data?.total_count || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                    position: ["bottomRight"],
                }}
                onTableChange={handleTableChange}
                maxHeight="calc(106vh - 300px)"
            />
        </div>
    );
};


export default UserPlanLogList;
